/**
 * AI Transparency Label
 *
 * Reusable disclosure badge/banner for AI-generated content.
 * Satisfies EU AI Act Art. 50 (limited-risk AI transparency).
 *
 * Variants:
 *  - "badge": Compact pill for inline use (e.g., next to scores)
 *  - "banner": Full-width notice bar for section headers
 */

import { View, Text, Pressable } from 'react-native';
import { useRouter } from 'expo-router';
import { Sparkles, ChevronRight } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { cn } from '@/lib/cn';

interface AiTransparencyLabelProps {
  /** Display variant */
  variant?: 'badge' | 'banner';
  /** Context passed as query param to the disclosure page */
  context?: 'scoring' | 'dd-chat' | 'insights' | 'team-analysis' | 'documents' | 'strategic-analysis';
  /** Additional className for outer container */
  className?: string;
}

export function AiTransparencyLabel({
  variant = 'badge',
  context,
  className,
}: AiTransparencyLabelProps) {
  const router = useRouter();
  const colors = useThemeColors();

  const handlePress = () => {
    const params = context ? `?context=${context}` : '';
    router.push(`/ai-disclosure${params}` as any);
  };

  if (variant === 'banner') {
    return (
      <Pressable
        onPress={handlePress}
        className={cn('flex-row items-center rounded-xl px-4 py-3 gap-2.5', className)}
        style={({ pressed }) => ({
          backgroundColor: colors.primary[50],
          borderWidth: 1,
          borderColor: colors.primary[100],
          opacity: pressed ? 0.8 : 1,
        })}
      >
        <Sparkles size={16} color={colors.primary[500]} />
        <Text
          style={{ color: colors.primary[700] }}
          className="text-xs font-medium flex-1"
        >
          This assessment was generated by an AI system.{' '}
          <Text style={{ color: colors.primary[500] }} className="font-semibold">
            Learn more
          </Text>
        </Text>
        <ChevronRight size={14} color={colors.primary[400]} />
      </Pressable>
    );
  }

  // Badge variant (default)
  return (
    <Pressable
      onPress={handlePress}
      className={cn('flex-row items-center rounded-full px-2.5 py-1 gap-1.5', className)}
      style={({ pressed }) => ({
        backgroundColor: colors.primary[50],
        borderWidth: 1,
        borderColor: colors.primary[100],
        opacity: pressed ? 0.8 : 1,
      })}
    >
      <Sparkles size={11} color={colors.primary[500]} />
      <Text
        style={{ color: colors.primary[600] }}
        className="text-xs font-medium"
      >
        AI-Generated
      </Text>
    </Pressable>
  );
}
